import { Box, Button, Container, Stack, Typography } from '@mui/material'
import { NavLink } from 'react-router-dom'

import { FOOTER_NAV_LIST, FOOTER_NAV_TYPE_LIST, SOCIAL_LIST, CONTACT_LIST } from '@/constants'
import { AppIcon } from '@/core'
import { useDeviceType } from '@/hooks'
import { scrollToSection } from '@/util'

import { FooterBox } from './footer.style'

// Keep the logo's 550×285 aspect ratio while scaling it down on smaller screens
const LOGO_RATIO = 285 / 550

export const Footer = () => {
  const { isSM, isMobile, isNotDesktop } = useDeviceType()

  const logoWidth = isSM ? 200 : isMobile ? 260 : isNotDesktop ? 380 : 550
  const logoHeight = Math.round(logoWidth * LOGO_RATIO)

  return (
    <FooterBox>
      <Container
        component={Stack}
        direction={{ xs: 'column', md: 'row' }}
        justifyContent='space-between'
        alignItems={{ xs: 'center', md: 'flex-start' }}
        gap={{ xs: 6, md: 4 }}
      >
        <NavLink to='/' aria-label='Digital Coast home'>
          <AppIcon name='logo' width={logoWidth} height={logoHeight} ariaLabel='Digital Coast logo' />
        </NavLink>

        <Stack alignItems={{ xs: 'center', md: 'flex-start' }} width={{ xs: '100%', md: 'auto' }}>
          <Typography
            component='h2'
            variant='h2'
            color='#4992FF'
            textAlign={{ xs: 'center', md: 'left' }}
            sx={{ fontSize: { xs: '28px', sm: '36px', md: '44px', lg: '50px' } }}
          >
            Work With Us
          </Typography>

          <Stack gap={1} mt={2} component='ul'>
            {CONTACT_LIST.map(item => (
              <Box key={item.value} component='li' sx={{ listStyle: 'none' }}>
                <Stack direction='row' alignItems='center' gap={1} justifyContent={{ xs: 'center', md: 'left' }}>
                  <AppIcon name={item.icon as any} size={20} ariaLabel={item.icon} />
                  <Typography color='textDisabled'>{item.value}</Typography>
                </Stack>
              </Box>
            ))}
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            flexWrap='wrap'
            gap={{ xs: 5, sm: 8, md: 20 }}
            mt={{ xs: 5, md: 10 }}
            alignItems={{ xs: 'center', sm: 'flex-start', md: 'flex-end' }}
          >
            {FOOTER_NAV_TYPE_LIST.map(item => (
              <Stack key={item} gap={{ xs: 2, md: 5 }} alignItems={{ xs: 'center', sm: 'flex-start' }}>
                <Typography textTransform='uppercase' fontFamily='Poppins' color='#4992FF'>
                  {item}
                </Typography>
                <Stack component='ul' gap={{ xs: 2, md: 5 }} m={0} p={0} sx={{ listStyle: 'none' }}>
                  {FOOTER_NAV_LIST[item].map(nav => (
                    <Box key={nav} component='li' sx={{ listStyle: 'none' }}>
                      <Button
                        type='button'
                        onClick={() => scrollToSection(nav)}
                        aria-label={nav}
                        sx={{
                          p: 0,
                          minWidth: 0,
                          fontFamily: 'Poppins',
                          textTransform: 'uppercase',
                          textAlign: { xs: 'center', sm: 'left' },
                          cursor: 'pointer',
                          transition: 'color 200ms ease',
                          border: 'none',
                          '&:hover': { color: '#4992FF', background: 'transparent' },
                          '&:focus-visible': {
                            outline: '2px solid #4992FF',
                            outlineOffset: '4px',
                            borderRadius: '4px'
                          }
                        }}
                      >
                        {nav}
                      </Button>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            ))}

            <Stack alignItems={{ xs: 'center', sm: 'flex-start' }} gap={2}>
              <Stack direction='row' gap='20px'>
                {SOCIAL_LIST.map(item => (
                  <a key={item.icon} href={item.link} aria-label={item.icon}>
                    <AppIcon name={item.icon} size={40} ariaLabel={item.icon} />
                  </a>
                ))}
              </Stack>
              <Typography fontFamily='Poppins' variant='body2'>
                © 2026 DigitalCoast
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </FooterBox>
  )
}
